import { useForm } from "react-hook-form";
import { Button, Form } from "@telluscompany/design-system";
import { Filter } from "lucide-react";

import { useLocalization } from "@i18n";

import type { GeneralReservoirManagementFilters } from "../../api/generalReservoirManagement/generalReservoirManagement.types";

export type GeneralReservoirManagementFilterData = {
  recordedOn: string;
};

const EMPTY_FORM: GeneralReservoirManagementFilterData = {
  recordedOn: "",
};

export type GeneralReservoirManagementFilterProps = {
  onFilter: (
    filters: Pick<GeneralReservoirManagementFilters, "recordedOn">,
  ) => void;
  onClear: () => void;
  initialFilters?: Partial<GeneralReservoirManagementFilterData>;
};

export const GeneralReservoirManagementFilter = ({
  onFilter,
  onClear,
  initialFilters,
}: GeneralReservoirManagementFilterProps) => {
  const { translate } = useLocalization();

  const form = useForm<GeneralReservoirManagementFilterData>({
    defaultValues: {
      recordedOn: initialFilters?.recordedOn ?? "",
    },
  });

  const handleFilter = (data: GeneralReservoirManagementFilterData) => {
    const filters: Pick<GeneralReservoirManagementFilters, "recordedOn"> = {};
    if (data.recordedOn.trim()) {
      filters.recordedOn = data.recordedOn.trim();
    }
    onFilter(filters);
  };

  const handleClear = () => {
    form.reset(EMPTY_FORM);
    onClear();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFilter)}>
        <div className="flex flex-column gap-5">
          <label className="group flex w-fit max-w-xs flex-col gap-1">
            <span className="text-label">
              {translate(
                "admin.structures.reservoirManagement.filter.label.recordedOn",
              )}
              :
            </span>
            <input
              type="date"
              aria-label={translate(
                "admin.structures.reservoirManagement.filter.label.recordedOn",
              )}
              data-testid="reservoir-management-filter-recorded-on"
              className="h-10 w-44 rounded-xl border border-border bg-background px-4 text-sm shadow-sm"
              {...form.register("recordedOn")}
            />
          </label>
        </div>
        <div className="mb-4 flex justify-end gap-4">
          <Button type="button" variant="secondary" onClick={handleClear}>
            {translate(
              "admin.structures.reservoirManagement.filter.button.clearFilters",
            )}
          </Button>
          <Button
            type="submit"
            data-testid="reservoir-management-filter-submit"
            iconLeft={<Filter fill="currentColor" />}
          >
            {translate(
              "admin.structures.reservoirManagement.table.filterButtonText",
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
